import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { Analytics, AnalyticsKpis, AnalyticsPeriod } from "../../api";
import {
  CHART_AXIS_PROPS,
  CHART_GRID_PROPS,
  CHART_TOOLTIP_PROPS,
  chartColor,
} from "../../lib/chart-colors";
import {
  fmtCompact,
  fmtDateLong,
  fmtDayMon,
  fmtHour,
  fmtMonYear,
  fmtMoney,
} from "../../lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Separator } from "../ui/separator";
import { ChangeBadge } from "./kpi-card";
import { RevenueDrivers } from "./revenue-drivers";

// Daromad grafigi X o'qi — bucket kalitlari (soat / YYYY-MM-DD / hafta / oy)
// o'rniga o'qiladigan sana. "Bugun" davrida soat bucket'i → "14:00", yil
// davrida oy bucket'i → "oy yy" (iyun 26), aks holda "kun-oy" (15-iyun). Intl
// `uz-UZ` ko'p muhitda oyni "M06" deb chiqargani uchun format helper'lari
// qo'lda o'zbekcha oy nomlarini ishlatadi.
function formatAxisDate(iso: string, period: AnalyticsPeriod): string {
  if (period === "day") return fmtHour(iso);
  return period === "year" ? fmtMonYear(iso) : fmtDayMon(iso);
}

/**
 * Daromad dinamikasi + "nega o'zgardi" bitta kartada.
 *
 * Grafik va uning izohi YONMA-YON turishi kerak: avval "qancha", darhol
 * ostida "nima hisobiga". Ilgari grafik yolg'iz turardi va sahifa faqat
 * raqam ko'rsatib, hech narsani tushuntirmasdi.
 */
export function RevenueChartCard({
  revenue,
  period,
  kpis,
  hasProducts,
}: {
  revenue: Analytics["revenue"];
  period: AnalyticsPeriod;
  kpis: AnalyticsKpis | null;
  hasProducts: boolean;
}) {
  const { t } = useTranslation("analytics");
  const series = useMemo(
    () =>
      (revenue.series || []).map((p, i) => ({
        date: p.date,
        amount: parseFloat(p.amount),
        prev: parseFloat(revenue.prev_series?.[i]?.amount || "0"),
      })),
    [revenue],
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="space-y-1">
            <CardTitle className="text-base">{t("revenueChart.title")}</CardTitle>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-num text-2xl font-bold tabular-nums">
                {fmtMoney(revenue.total)}
              </span>
              <ChangeBadge pct={revenue.change_pct} />
            </div>
            <p className="text-xs text-muted-foreground">
              {t("revenueChart.prevPeriod", { value: fmtMoney(revenue.prev_total) })}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="w-full h-[220px] sm:h-[280px]">
          <ResponsiveContainer>
            <AreaChart data={series} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={chartColor("chart-1")} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={chartColor("chart-1")} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...CHART_GRID_PROPS} vertical={false} />
              <XAxis
                dataKey="date"
                {...CHART_AXIS_PROPS}
                tickFormatter={(v: string) => formatAxisDate(v, period)}
                interval="preserveStartEnd"
                minTickGap={24}
                tickMargin={8}
              />
              <YAxis {...CHART_AXIS_PROPS} tickFormatter={fmtCompact} width={48} />
              <Tooltip
                {...CHART_TOOLTIP_PROPS}
                formatter={(v: number) => fmtMoney(v)}
                labelFormatter={(label: string) => fmtDateLong(label)}
              />
              {/* O'tgan davr ATAYIN ikkinchi darajali: to'ldirishsiz, ingichka
                  va uzuq chiziq. Joriy davr — to'q chiziq + gradient. */}
              <Area
                type="monotone"
                name={t("revenueChart.prevSeries")}
                dataKey="prev"
                stroke={chartColor("chart-neutral")}
                strokeDasharray="4 4"
                strokeWidth={1.5}
                fill="transparent"
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                name={t("revenueChart.currentSeries")}
                dataKey="amount"
                stroke={chartColor("chart-1")}
                strokeWidth={2}
                fill="url(#revGrad)"
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {hasProducts && kpis && (
          <>
            <Separator />
            <RevenueDrivers kpis={kpis} hasProducts={hasProducts} />
          </>
        )}
      </CardContent>
    </Card>
  );
}
